import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import ImageExtension from '@tiptap/extension-image';
import LinkExtension from '@tiptap/extension-link';
import { useEffect, useRef } from 'react';
import Box from '@mui/material/Box';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import ToggleButton from '@mui/material/ToggleButton';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import ImageIcon from '@mui/icons-material/Image';
import LinkIcon from '@mui/icons-material/Link';

interface IntroPageEditorProps {
  value: string;
  onChange: (html: string) => void;
  onUploadImage: (file: File) => Promise<string>;
}

/**
 * Rich text editor dùng chung cho trang giới thiệu & hướng dẫn sử dụng.
 * Toolbar tối giản: bold/italic/danh sách/ảnh/link — đủ cho nội dung
 * marketing/hướng dẫn, không cần bảng biểu hay định dạng phức tạp.
 */
export function IntroPageEditor({ value, onChange, onUploadImage }: IntroPageEditorProps): JSX.Element {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const editor = useEditor({
    extensions: [StarterKit, ImageExtension, LinkExtension.configure({ openOnClick: false })],
    content: value,
    onUpdate: ({ editor: e }) => onChange(e.getHTML()),
  });

  // Đồng bộ khi `value` đổi từ bên ngoài (vd load dữ liệu từ API) — Tiptap
  // không tự re-render theo prop, phải set nội dung thủ công.
  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value, false);
    }
  }, [value, editor]);

  const handleImagePick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !editor) return;
    const url = await onUploadImage(file);
    editor.chain().focus().setImage({ src: url }).run();
  };

  if (!editor) return <Box />;

  return (
    <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
      <ToggleButtonGroup size="small" sx={{ p: 0.5, borderBottom: 1, borderColor: 'divider' }}>
        <ToggleButton
          value="bold"
          selected={editor.isActive('bold')}
          onClick={() => editor.chain().focus().toggleBold().run()}
        >
          <FormatBoldIcon fontSize="small" />
        </ToggleButton>
        <ToggleButton
          value="italic"
          selected={editor.isActive('italic')}
          onClick={() => editor.chain().focus().toggleItalic().run()}
        >
          <FormatItalicIcon fontSize="small" />
        </ToggleButton>
        <ToggleButton
          value="bulletList"
          selected={editor.isActive('bulletList')}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
        >
          <FormatListBulletedIcon fontSize="small" />
        </ToggleButton>
        <ToggleButton value="image" onClick={() => fileInputRef.current?.click()}>
          <ImageIcon fontSize="small" />
        </ToggleButton>
        <ToggleButton
          value="link"
          selected={editor.isActive('link')}
          onClick={() => {
            const url = window.prompt('Nhập URL liên kết:');
            if (url) editor.chain().focus().setLink({ href: url }).run();
          }}
        >
          <LinkIcon fontSize="small" />
        </ToggleButton>
      </ToggleButtonGroup>
      <input
        type="file"
        accept="image/png,image/jpeg,image/webp"
        hidden
        ref={fileInputRef}
        onChange={(e) => void handleImagePick(e)}
      />
      <Box sx={{ p: 2, minHeight: 240, '& .ProseMirror': { outline: 'none' }, '& img': { maxWidth: '100%' } }}>
        <EditorContent editor={editor} />
      </Box>
    </Box>
  );
}
